import api from './api';
import type { 
  Notification, 
  TaskReminder, 
  CreateTaskReminderData, 
  UpdateTaskReminderData, 
  NotificationFilters, 
  NotificationResponse, 
  NotificationCountResponse 
} from '../types/notification';

// Notification endpoints
export const getNotifications = async (
  agencySlug: string, 
  filters?: NotificationFilters, 
  page: number = 1, 
  limit: number = 20
): Promise<NotificationResponse> => {
  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
    ...(filters?.category && { category: filters.category }),
    ...(filters?.isRead !== undefined && { isRead: filters.isRead.toString() }),
    ...(filters?.priority && { priority: filters.priority }),
    ...(filters?.type && { type: filters.type })
  });

  const res = await api.get<NotificationResponse>(`/agencies/${agencySlug}/notifications?${params}`);
  return res.data;
};

export const getNotificationCount = async (agencySlug: string): Promise<NotificationCountResponse> => {
  const res = await api.get<NotificationCountResponse>(`/agencies/${agencySlug}/notifications/count`);
  return res.data;
};

export const markNotificationAsRead = async (agencySlug: string, notificationId: string): Promise<Notification> => {
  const res = await api.put<Notification>(`/agencies/${agencySlug}/notifications/${notificationId}/read`);
  return res.data;
};

export const markAllNotificationsAsRead = async (agencySlug: string): Promise<{ message: string }> => {
  const res = await api.put<{ message: string }>(`/agencies/${agencySlug}/notifications/read-all`);
  return res.data;
};

export const archiveNotification = async (agencySlug: string, notificationId: string): Promise<Notification> => {
  const res = await api.put<Notification>(`/agencies/${agencySlug}/notifications/${notificationId}/archive`);
  return res.data;
};

// Task reminder endpoints
export const createTaskReminder = async (agencySlug: string, data: CreateTaskReminderData): Promise<TaskReminder> => {
  const res = await api.post<TaskReminder>(`/agencies/${agencySlug}/reminders`, data);
  return res.data;
};

export const getTaskReminders = async (
  agencySlug: string, 
  workflowId?: string, 
  propertyId?: string, 
  isActive?: boolean
): Promise<TaskReminder[]> => {
  const params = new URLSearchParams();
  if (workflowId) params.append('workflowId', workflowId);
  if (propertyId) params.append('propertyId', propertyId);
  if (isActive !== undefined) params.append('isActive', isActive.toString());

  const res = await api.get<TaskReminder[]>(`/agencies/${agencySlug}/reminders?${params}`);
  return res.data;
};

export const updateTaskReminder = async (
  agencySlug: string, 
  reminderId: string, 
  data: UpdateTaskReminderData
): Promise<TaskReminder> => {
  const res = await api.put<TaskReminder>(`/agencies/${agencySlug}/reminders/${reminderId}`, data);
  return res.data;
};

export const deleteTaskReminder = async (agencySlug: string, reminderId: string): Promise<{ message: string }> => {
  const res = await api.delete<{ message: string }>(`/agencies/${agencySlug}/reminders/${reminderId}`);
  return res.data;
}; 